import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import { Button, ButtonWithTimer } from '@hostfully/ui-components';
import {
  ButtonContainer,
  FieldsContainer,
  StyledFormFieldset,
} from './MFACodeForm.styles';
import type { MFACodeFormProps } from './MFACodeForm.types';
import PinCodeField from '@/components/forms/PinCodeField';

const CODE_LENGTH = 6;

const MFACodeForm = ({
  isSubmitting,
  isResending,
  handleResendCode,
  resendCooldownSeconds = 0,
  helperText,
  submitLabel,
  fieldName = 'pinCode',
}: MFACodeFormProps) => {
  const { t } = useTranslation();
  const { control, clearErrors } = useFormContext<FieldValues>();
  const fieldValue = (useWatch({ control, name: fieldName }) as string) ?? '';
  const fieldsRef = useRef<HTMLDivElement>(null);
  const autoSubmittedRef = useRef(false);

  const clearFieldError = () => clearErrors(fieldName);

  // Submit as soon as the code is complete, whether it was typed or pasted.
  // The flag is reset when the code becomes incomplete again, so correcting a
  // rejected code re-submits without the user having to click.
  useEffect(() => {
    if (fieldValue.length < CODE_LENGTH) {
      autoSubmittedRef.current = false;
      return;
    }

    if (autoSubmittedRef.current || isSubmitting) {
      return;
    }

    autoSubmittedRef.current = true;
    fieldsRef.current?.closest('form')?.requestSubmit();
  }, [fieldValue, isSubmitting]);

  return (
    <StyledFormFieldset disabled={isSubmitting}>
      <FieldsContainer ref={fieldsRef}>
        {helperText}
        <PinCodeField
          name={fieldName}
          length={CODE_LENGTH}
          disabled={isSubmitting}
          clearErrors={clearFieldError}
        />
        <ButtonContainer>
          {handleResendCode && (
            <ButtonWithTimer
              data-testid="resend-code-button"
              type="button"
              size="large"
              variant="textPrimary"
              // Keeps a second code from being requested while one is on its
              // way, or while the entered code is being confirmed.
              disabled={isResending || isSubmitting}
              onClick={handleResendCode}
              remainingSeconds={resendCooldownSeconds}
              countdownLabel={(seconds) => t('pageLogin.resendIn', { seconds })}
            >
              {t('pageLogin.requestNewCode')}
            </ButtonWithTimer>
          )}
          <Button
            data-testid="submit-button"
            disabled={fieldValue.length < CODE_LENGTH || isSubmitting}
            type="submit"
            size="large"
          >
            {submitLabel ?? t('pageLogin.confirmCode')}
          </Button>
        </ButtonContainer>
      </FieldsContainer>
    </StyledFormFieldset>
  );
};

export default MFACodeForm;
